const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export function isUuid(value: string): boolean {
  return UUID_RE.test(value)
}

export function ticketNumberFromDisplayId(displayId: string): string {
  const idx = displayId.lastIndexOf('-')
  return idx >= 0 ? displayId.slice(idx + 1) : displayId
}

export function boardPath(board: { shortName?: string | null; id: string }): string {
  return `/boards/${board.shortName ?? board.id}`
}

/**
 * Keeps board-local UI state while navigating between a board and its tickets.
 * Other routes and other boards retain their own remount boundary.
 */
export function routeStateKey(pathname: string): string {
  const segments = pathname.split('/')
  return segments[1] === 'boards' && segments[2]
    ? `/boards/${segments[2]}`
    : pathname
}

export function boardSettingsPath(board: { shortName?: string | null; id: string }): string {
  return `${boardPath(board)}/settings`
}

export function ticketPath(
  board: { shortName: string },
  ticket: { displayId: string },
): string {
  return `/boards/${board.shortName}/${ticketNumberFromDisplayId(ticket.displayId)}`
}

export function ticketPathFromDisplayId(displayId: string): string {
  const idx = displayId.lastIndexOf('-')
  if (idx < 0) return `/boards/${displayId}`
  const shortName = displayId.slice(0, idx)
  const number = displayId.slice(idx + 1)
  return `/boards/${shortName}/${number}`
}

/** Legacy UUID segments — Board page canonicalizes after load. */
export function ticketPathFromIds(boardId: string, ticketId: string): string {
  return `/boards/${boardId}/${ticketId}`
}

export function findTicketOnBoard<T extends { id: string; displayId: string }>(
  tickets: T[],
  ticketKey: string,
  boardShortName: string,
): T | undefined {
  const key = ticketKey.trim()
  if (!key) return undefined

  const byId = tickets.find((ticket) => ticket.id === key)
  if (byId) return byId

  const upperKey = key.toUpperCase()
  const byDisplayId = tickets.find((ticket) => ticket.displayId.toUpperCase() === upperKey)
  if (byDisplayId) return byDisplayId

  if (/^\d+$/.test(key)) {
    const normalized = String(parseInt(key, 10)).padStart(4, '0')
    const composed = `${boardShortName.toUpperCase()}-${normalized}`
    return tickets.find((ticket) => ticket.displayId.toUpperCase() === composed)
  }

  return undefined
}

export function buildShareTicketUrl(displayId: string): string {
  if (typeof window === 'undefined') return ticketPathFromDisplayId(displayId)
  return `${window.location.origin}${ticketPathFromDisplayId(displayId)}`
}
